import { useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { BusyIcon } from '../../components/loading';

/**
 * Monthly Summary Repository — "AutoGenerate" dialog (ALPACA-LIMS parity).
 * Picks a Year + Month (+ Overwrite Existing) and bulk-creates one MSR per
 * active category via POST /auto-generate.  Mirrors the legacy
 * MSRController.AutoGenerateMSRAction dialog.
 * (Merged team package 2026-07-02; buttons/notifications conformed to the
 * shared `.ca-btn` / `useNotify` / `<BusyIcon>` standard — CLAUDE.md §18.6.)
 */
interface Props {
  onClose: () => void;
  onDone: () => void;
}

const ALL_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function MsrAutoGenerateDialog({ onClose, onDone }: Props) {
  const notify = useNotify();
  const now = new Date();

  const [year,      setYear]      = useState(String(now.getFullYear()));
  const [month,     setMonth]     = useState(now.getMonth());   // 0-based
  const [overwrite, setOverwrite] = useState(false);
  const [saving,    setSaving]    = useState(false);

  const handleOk = async () => {
    if (!year.trim()) { notify.warning('Year is required.'); return; }
    setSaving(true);
    try {
      const res = await api.post<{ message?: string }>(
        '/api/v1/monthly-summary-repository/auto-generate',
        { year: year.trim(), month, overwriteExisting: overwrite });
      const msg: string = res.data?.message ?? 'AutoGenerate complete.';
      notify.success(msg);
      onDone();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message ?? 'AutoGenerate failed.';
      notify.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="qs-overlay" onMouseDown={onClose}>
      <div className="msr-ag-dialog" onMouseDown={(e) => e.stopPropagation()}>
        {/* Title bar */}
        <div className="qs-titlebar">
          <span className="qs-title">
            <i className="ti ti-sparkles" /> AutoGenerate
          </span>
          <i className="ti ti-x qs-close" title="Close" onClick={onClose} />
        </div>

        {/* Body */}
        <div className="msr-ag-body">
          <div className="msr-ag-row">
            <label className="msr-ag-label">Year</label>
            <input
              className="qs-input msr-ag-year"
              type="text"
              value={year}
              maxLength={4}
              onChange={(e) => setYear(e.target.value.replace(/\D/g, ''))}
            />
          </div>

          <div className="msr-ag-row">
            <label className="msr-ag-label">Month</label>
            <select
              className="qs-input msr-ag-month"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
            >
              {ALL_MONTHS.map((m, i) => (
                <option key={m} value={i}>{m}</option>
              ))}
            </select>
          </div>

          <div className="msr-ag-row msr-ag-chk-row">
            <label className="msr-ag-chk-label">
              <input
                type="checkbox"
                checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
              />
              <span>Overwrite Existing</span>
            </label>
          </div>
        </div>

        {/* Footer — shared .ca-btn family + prototype glyphs (BO UI Standard). */}
        <div className="msr-ag-footer">
          <button className="ca-btn ca-btn-solid" onClick={handleOk} disabled={saving}>
            {saving ? <BusyIcon busy icon="ti-sparkles" /> : <span className="tb-i">💾</span>}
            {saving ? ' Processing…' : ' OK'}
          </button>
          <button className="ca-btn" onClick={onClose} disabled={saving}>
            <span className="tb-i tb-i-grey">↩︎</span> Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
